import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Info, Radio } from "lucide-react";
import { api } from "../lib/api";

export default function HeroBanner({ video }) {
  const nav = useNavigate();
  const [liveRail, setLiveRail] = useState([]);
  const [liveIdx, setLiveIdx] = useState(0);
  const [showLive, setShowLive] = useState(false);

  // Poll GCR Theaters every 30 s; rotate billboard every 12 s between VOD hero and live programs
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/gcr/now-rail");
        if (!cancelled) setLiveRail(data || []);
      } catch (e) {
        console.warn("[hero] now-rail fetch failed", e?.response?.status);
      }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (liveRail.length === 0) { setShowLive(false); return; }
    // Cycle: VOD hero (12 s) → live #1 (8 s) → live #2 (8 s) → VOD again
    const cycle = setInterval(() => {
      setShowLive((live) => {
        if (!live) return true;
        // already on a live slot — advance index, return to VOD when wrapped
        setLiveIdx((i) => {
          const next = i + 1;
          if (next >= liveRail.length) {
            // wrap: jump back to VOD
            setTimeout(() => setShowLive(false), 0);
            return 0;
          }
          return next;
        });
        return true;
      });
    }, 8000);
    return () => clearInterval(cycle);
  }, [liveRail.length]);

  if (!video) return null;

  const live = showLive && liveRail.length > 0 ? liveRail[liveIdx % liveRail.length] : null;
  const bg = live ? (live.backdrop || live.poster) : video.backdrop;
  const title = live ? live.title : video.title;
  const description = live ? `Tune in now on ${live.name} — premium 24/7 channel.` : video.description;

  return (
    <div data-testid="hero-banner" className="relative h-[85vh] min-h-[520px] w-full overflow-hidden">
      <img
        key={bg}
        src={bg}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover animate-[fade-in_700ms_ease]"
      />
      <div className="absolute inset-0 hero-gradient" />
      <div className="relative z-10 h-full flex flex-col justify-end pb-24 px-6 lg:px-12 max-w-3xl">
        {live ? (
          <div data-testid="hero-live-pill" className="mb-4 animate-fade-up flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E50914] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#E50914]"></span>
            </span>
            <span className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: live.color || "#E50914" }}>
              📡 Live · {live.name}
            </span>
          </div>
        ) : (
          <div className="mb-4 animate-fade-up">
            <span className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold">
              {video.featured ? "Featured" : video.type === "series" ? "Series" : "Movie"}
            </span>
          </div>
        )}
        <h1 key={title} className="text-5xl md:text-7xl font-black text-white tracking-tighter animate-fade-up" style={{ animationDelay: "80ms" }}>
          {title}
        </h1>
        {!live && (
          <div className="flex items-center gap-4 mt-4 text-sm text-gray-300 animate-fade-up" style={{ animationDelay: "160ms" }}>
            <span className="text-green-400 font-semibold">{Math.round((video.tmdb_rating || 7) * 10)}% Match</span>
            <span>{video.year}</span>
            <span className="border border-white/30 px-2 py-0.5 text-xs">{video.rating}</span>
            <span>{video.duration_min} min</span>
          </div>
        )}
        <p className="mt-4 text-lg text-gray-200 line-clamp-3 animate-fade-up max-w-xl" style={{ animationDelay: "240ms" }}>
          {description}
        </p>
        <div className="flex items-center gap-3 mt-6 animate-fade-up" style={{ animationDelay: "320ms" }}>
          <button
            data-testid={live ? "hero-tune-btn" : "hero-play-btn"}
            onClick={() => nav(live ? `/live/${live.slug}` : `/watch/${video.id}`)}
            className="btn-red font-semibold px-8 py-3 rounded flex items-center gap-2 text-lg"
          >
            {live ? <Radio className="w-5 h-5" /> : <Play className="w-5 h-5 fill-current" />}
            {live ? "Tune In" : "Play"}
          </button>
          <button
            data-testid="hero-info-btn"
            onClick={() => nav(live ? `/live` : `/title/${video.id}`)}
            className="btn-glass font-semibold px-8 py-3 rounded flex items-center gap-2 text-lg"
          >
            <Info className="w-5 h-5" /> {live ? "Channel Guide" : "More Info"}
          </button>
        </div>
      </div>
      <div className="absolute bottom-8 right-6 lg:right-12 z-10">
        <span className="border-l-4 border-[#E50914] pl-3 text-sm text-gray-300 uppercase tracking-wider">
          {live ? "Live Now" : "Now Streaming"}
        </span>
      </div>
    </div>
  );
}
